import { endOfDay, isValid, parseISO, startOfDay } from 'date-fns';
import { useCallback, useState } from 'react';
import { Case } from '../types/Case/Case.type';

const DEFAULT_DATE_RANGE_YEARS = 5;

interface UseCalculatedDateRangeOptions {
  caseData?: Case;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
}

function computeDateBounds(offenseDate: string | undefined | null) {
  let calculatedMin: Date | null = null;
  let calculatedMax: Date | null = null;
  let defaultStart: Date | null = null;
  let defaultEnd: Date | null = null;

  const setDefaultDateRange = () => {
    calculatedMin = startOfDay(new Date(1900, 0, 1));
    calculatedMax = endOfDay(new Date(2099, 11, 31));
    defaultStart = calculatedMin;
    defaultEnd = calculatedMax;
  };

  if (offenseDate) {
    const filingDate = parseISO(offenseDate);
    if (isValid(filingDate)) {
      const filingYear = filingDate.getFullYear();
      const minYear = filingYear - DEFAULT_DATE_RANGE_YEARS;
      const maxYear = filingYear + DEFAULT_DATE_RANGE_YEARS;
      calculatedMin = startOfDay(new Date(minYear, 0, 1));
      calculatedMax = endOfDay(new Date(maxYear, 11, 31));
      defaultStart = calculatedMin;
      defaultEnd = calculatedMax;
    } else {
      setDefaultDateRange();
    }
  } else {
    setDefaultDateRange();
  }

  return {
    minDate: calculatedMin,
    maxDate: calculatedMax,
    defaultCaseStartDate: defaultStart,
    defaultCaseEndDate: defaultEnd,
  };
}

const UNSET = Symbol('unset');

export const useCalculatedDateRange = ({
  caseData,
  initialStartDate: propInitialStartDate,
  initialEndDate: propInitialEndDate,
}: UseCalculatedDateRangeOptions) => {
  const { minDate, maxDate, defaultCaseStartDate, defaultCaseEndDate } = computeDateBounds(caseData?.offense_date);

  const [startDateOverride, setStartDateOverride] = useState<Date | null | typeof UNSET>(UNSET);
  const [endDateOverride, setEndDateOverride] = useState<Date | null | typeof UNSET>(UNSET);

  const startDate = startDateOverride !== UNSET ? startDateOverride : propInitialStartDate || defaultCaseStartDate;
  const endDate = endDateOverride !== UNSET ? endDateOverride : propInitialEndDate || defaultCaseEndDate;

  const handleStartDateChange = useCallback(
    (newValue: Date | null) => {
      setStartDateOverride(newValue);
      if (newValue && endDate && newValue > endDate) {
        setEndDateOverride(newValue);
      }
    },
    [endDate],
  );

  const handleEndDateChange = useCallback(
    (newValue: Date | null) => {
      setEndDateOverride(newValue);
      if (newValue && startDate && newValue < startDate) {
        setStartDateOverride(newValue);
      }
    },
    [startDate],
  );

  const resetDates = useCallback(() => {
    setStartDateOverride(UNSET);
    setEndDateOverride(UNSET);
  }, []);

  return {
    startDate,
    setStartDate: handleStartDateChange,
    endDate,
    setEndDate: handleEndDateChange,
    minDate,
    maxDate,
    resetDates,
  };
};
